/**
 * Swipe-to-reveal row actions.
 *
 * Shared by the PM thread list, message requests and the bookmarks list. Touch
 * devices reveal a panel of actions by swiping a row leftward. Pointer devices
 * get an always-visible action block instead, since there is no swipe gesture
 * available to them.
 *
 * Markup contract:
 *
 *   <div data-swipe-row data-swipe-id="42" class="relative overflow-hidden">
 *     ...row content...
 *     <div data-swipe-desktop>
 *       <button>...</button>
 *     </div>
 *     <div data-swipe-panel
 *          class="hidden absolute inset-y-0 right-0 flex items-center transform
 *                 translate-x-full transition-transform duration-300 ease-out">
 *       <button data-swipe-action="delete">...</button>
 *     </div>
 *   </div>
 *
 * Activating a panel button dispatches a bubbling `swipe-action` CustomEvent on
 * the row, carrying { action, id, row, button }. Consumers listen for that event
 * rather than this module knowing about any particular feature.
 *
 * The reveal is a class swap animated by a CSS transition, not a finger-tracking
 * drag. Rows stay cheap to paint when a long list is scrolling.
 */
(function() {
    'use strict';

    var ROW = '[data-swipe-row]';
    var PANEL = '[data-swipe-panel]';
    var DESKTOP = '[data-swipe-desktop]';
    var ACTION = '[data-swipe-panel] [data-swipe-action]';

    // Leftward travel that opens the panel.
    var REVEAL_THRESHOLD_PX = 30;
    // Rightward travel that closes it again.
    var HIDE_THRESHOLD_PX = 10;
    // Travel before the gesture is treated as horizontal at all.
    var DIRECTION_LOCK_PX = 10;
    // Vertical travel that hands the gesture back to scrolling.
    var VERTICAL_ABORT_PX = 30;
    // Grace period before a finished swipe stops suppressing the row's click.
    var DRAG_RELEASE_MS = 100;
    var RESIZE_DEBOUNCE_MS = 150;

    var touchStartX = 0;
    var touchStartY = 0;
    var currentRow = null;
    var actionHandled = false;

    function isTouchDevice() {
        return ('ontouchstart' in window) ||
               (navigator.maxTouchPoints > 0) ||
               (navigator.msMaxTouchPoints > 0);
    }

    function isMobileDevice() {
        if (typeof window.is_mobile_browser === 'function') {
            return window.is_mobile_browser();
        }
        var userAgent = navigator.userAgent || navigator.vendor || window.opera;
        return (
            /android/i.test(userAgent) ||
            /iPad|iPhone|iPod/.test(userAgent) ||
            /windows phone/i.test(userAgent) ||
            /mobile/i.test(userAgent)
        );
    }

    function shouldUseSwipeInterface() {
        return isMobileDevice() || (isTouchDevice() && window.innerWidth < 768);
    }

    function closeAll() {
        $(PANEL).removeClass('translate-x-0').addClass('translate-x-full');
    }

    /**
     * Show the interface that matches the current device, and re-run Lucide over
     * newly shown panels so their icons render.
     */
    function refresh() {
        if (shouldUseSwipeInterface()) {
            $(DESKTOP).addClass('hidden');
            $(PANEL).removeClass('hidden').each(function() {
                window.LucideIcons && window.LucideIcons.initialize(this);
            });
        } else {
            $(DESKTOP).removeClass('hidden');
            $(PANEL).addClass('hidden');
        }
    }

    function dispatchAction(button) {
        var $button = $(button);
        var $row = $button.closest(ROW);
        if (!$row.length) {
            return;
        }

        var row = $row[0];

        // Read the id as a raw attribute. jQuery's .data() coerces numeric
        // strings to numbers, which silently corrupts ids above 2^53.
        row.dispatchEvent(new CustomEvent('swipe-action', {
            bubbles: true,
            detail: {
                action: $button.attr('data-swipe-action'),
                id: row.getAttribute('data-swipe-id'),
                row: row,
                button: button
            }
        }));
    }

    $(document).ready(function() {
        refresh();

        var resizeTimer;
        $(window).on('resize', function() {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(refresh, RESIZE_DEBOUNCE_MS);
        });

        $(document).on('touchstart', ROW, function(e) {
            if (!shouldUseSwipeInterface()) return;

            var touch = e.originalEvent.touches[0];
            touchStartX = touch.clientX;
            touchStartY = touch.clientY;
            window.isDragging = false;
            currentRow = $(this);

            // Only one row may be open at a time.
            closeAll();
        });

        $(document).on('touchmove', ROW, function(e) {
            if (!shouldUseSwipeInterface() || !currentRow || !currentRow.is($(this))) return;

            var touch = e.originalEvent.touches[0];
            var deltaX = touchStartX - touch.clientX;
            var deltaY = Math.abs(touchStartY - touch.clientY);

            if (deltaY > VERTICAL_ABORT_PX) return;

            if (Math.abs(deltaX) > DIRECTION_LOCK_PX) {
                window.isDragging = true;
                e.preventDefault();

                var $panel = $(this).find(PANEL);

                if (deltaX > REVEAL_THRESHOLD_PX) {
                    $panel.removeClass('translate-x-full').addClass('translate-x-0');
                } else if (deltaX < -HIDE_THRESHOLD_PX) {
                    $panel.removeClass('translate-x-0').addClass('translate-x-full');
                }
            }
        });

        $(document).on('touchend', ROW, function() {
            if (!shouldUseSwipeInterface()) return;

            // Held briefly so the click synthesised at the end of a swipe is
            // still recognisable as part of the gesture by row-click handlers.
            setTimeout(function() {
                window.isDragging = false;
                currentRow = null;
            }, DRAG_RELEASE_MS);
        });

        // The OS can claim a gesture, and touchend never arrives. A cancel
        // synthesises no click, so the flag clears without the release delay.
        $(document).on('touchcancel', ROW, function() {
            if (!shouldUseSwipeInterface()) return;

            window.isDragging = false;
            currentRow = null;
        });

        // Touching anywhere outside a row or panel dismisses what is open.
        $(document).on('touchstart', function(e) {
            if (!shouldUseSwipeInterface()) return;

            if (!$(e.target).closest(ROW).length && !$(e.target).closest(PANEL).length) {
                closeAll();
            }
        });

        // Touch is the primary path and click is the pointer fallback. The flag
        // stops a touch that also synthesises a click from firing the action
        // twice.
        $(document).on('touchstart', ACTION, function() {
            actionHandled = false;
        });

        $(document).on('touchend', ACTION, function(e) {
            if (actionHandled) return false;

            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            actionHandled = true;
            dispatchAction(this);

            return false;
        });

        $(document).on('click', ACTION, function(e) {
            if (actionHandled) {
                actionHandled = false;
                return false;
            }

            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            dispatchAction(this);

            return false;
        });
    });

    window.SwipeActions = {
        refresh: refresh,
        closeAll: closeAll,
        shouldUseSwipeInterface: shouldUseSwipeInterface
    };
})();
